import { useEffect, useState, useCallback } from 'react'
import { contactsAPI } from '@utils/api'
import AdminLayout from '@components/admin/AdminLayout'
import {
  Search, Trash2, Loader2, AlertCircle, ChevronLeft, ChevronRight,
  ShieldBan, ShieldCheck, Eye, X, MessageSquare, Mail, Globe,
  Phone, MapPin, Clock, RefreshCw, Filter
} from 'lucide-react'
import { format } from 'date-fns'
import toast from 'react-hot-toast'

const STATUSES = ['new', 'read', 'replied', 'spam']

const statusStyle = {
  new: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  read: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
  replied: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  spam: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
}

const sourceStyle = {
  popup: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
  contact_page: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400',
  other: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
}

const fi = 'w-full px-3.5 py-2.5 rounded-xl text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all'

const DetailModal = ({ contact, onClose, onUpdated }) => {
  const [status, setStatus] = useState(contact.status)
  const [notes, setNotes] = useState(contact.notes || '')
  const [saving, setSaving] = useState(false)
  const [blocking, setBlocking] = useState(false)

  const save = async () => {
    setSaving(true)
    try {
      await contactsAPI.update(contact._id, { status, notes })
      toast.success('Updated')
      onUpdated()
      onClose()
    } catch { toast.error('Update failed') }
    finally { setSaving(false) }
  }

  const blockEmail = async () => {
    setBlocking(true)
    try {
      await contactsAPI.blockEmail(contact.email)
      toast.success(`Email ${contact.email} blocked`)
      onUpdated()
      onClose()
    } catch { toast.error('Block failed') }
    finally { setBlocking(false) }
  }

  const blockIp = async () => {
    setBlocking(true)
    try {
      await contactsAPI.blockIp(contact.ip)
      toast.success(`IP ${contact.ip} blocked`)
      onUpdated()
      onClose()
    } catch { toast.error('Block failed') }
    finally { setBlocking(false) }
  }

  const unblockEmail = async () => {
    setBlocking(true)
    try {
      await contactsAPI.unblockEmail(contact.email)
      toast.success(`Email ${contact.email} unblocked`)
      onUpdated()
      onClose()
    } catch { toast.error('Unblock failed') }
    finally { setBlocking(false) }
  }

  const unblockIp = async () => {
    setBlocking(true)
    try {
      await contactsAPI.unblockIp(contact.ip)
      toast.success(`IP ${contact.ip} unblocked`)
      onUpdated()
      onClose()
    } catch { toast.error('Unblock failed') }
    finally { setBlocking(false) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-lg my-6 border border-gray-200 dark:border-gray-800">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-primary-500 flex items-center justify-center">
              <MessageSquare className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900 dark:text-white">{contact.name}</h2>
              <p className="text-xs text-gray-500">{contact.email}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-400">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            {contact.phone && (
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <Phone className="w-4 h-4 shrink-0 text-gray-400" />
                <span>{contact.phone}</span>
              </div>
            )}
            {contact.country && (
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <MapPin className="w-4 h-4 shrink-0 text-gray-400" />
                <span>{contact.country}</span>
              </div>
            )}
            {contact.ip && (
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <Globe className="w-4 h-4 shrink-0 text-gray-400" />
                <span className="font-mono text-xs">{contact.ip}</span>
              </div>
            )}
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
              <Clock className="w-4 h-4 shrink-0 text-gray-400" />
              <span>{format(new Date(contact.createdAt), 'dd MMM yyyy, h:mm a')}</span>
            </div>
          </div>

          <div className="flex gap-2 flex-wrap">
            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${sourceStyle[contact.source] || sourceStyle.other}`}>
              {contact.source?.replace('_', ' ')}
            </span>
            {contact.blockedEmail && (
              <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">Email Blocked</span>
            )}
            {contact.blockedIp && (
              <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">IP Blocked</span>
            )}
          </div>

          {contact.message && (
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Message</p>
              <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{contact.message}</p>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1.5">Status</label>
            <select value={status} onChange={e => setStatus(e.target.value)} className={fi}>
              {STATUSES.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1.5">Admin Notes</label>
            <textarea rows={3} value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Internal notes…"
              className={`${fi} resize-none`} />
          </div>

          <div className="pt-1 border-t border-gray-100 dark:border-gray-800">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Spam Controls</p>
            <div className="flex flex-wrap gap-2">
              {contact.blockedEmail ? (
                <button onClick={unblockEmail} disabled={blocking}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 text-xs font-medium hover:bg-green-100 transition-colors">
                  <ShieldCheck className="w-3.5 h-3.5" /> Unblock Email
                </button>
              ) : (
                <button onClick={blockEmail} disabled={blocking}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-xs font-medium hover:bg-red-100 transition-colors">
                  <ShieldBan className="w-3.5 h-3.5" /> Block Email
                </button>
              )}
              {contact.ip && (contact.blockedIp ? (
                <button onClick={unblockIp} disabled={blocking}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 text-xs font-medium hover:bg-green-100 transition-colors">
                  <ShieldCheck className="w-3.5 h-3.5" /> Unblock IP
                </button>
              ) : (
                <button onClick={blockIp} disabled={blocking}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-xs font-medium hover:bg-red-100 transition-colors">
                  <ShieldBan className="w-3.5 h-3.5" /> Block IP {contact.ip}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-100 dark:border-gray-800">
          <button onClick={onClose} className="px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
            Close
          </button>
          <button onClick={save} disabled={saving}
            className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-60 transition-all">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save Changes
          </button>
        </div>
      </div>
    </div>
  )
}

const AdminContacts = () => {
  const [contacts, setContacts] = useState([])
  const [stats, setStats] = useState({ total: 0, new: 0, replied: 0, spam: 0 })
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 })
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [filterSource, setFilterSource] = useState('')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState(null)

  const fetchContacts = useCallback(async () => {
    setLoading(true)
    try {
      const res = await contactsAPI.getAll({
        page, limit: 20,
        search: search || undefined,
        status: filterStatus || undefined,
        source: filterSource || undefined,
      })
      setContacts(res.data || [])
      setPagination(res.pagination || { page: 1, pages: 1, total: 0 })
    } catch { toast.error('Failed to load contacts') }
    finally { setLoading(false) }
  }, [page, search, filterStatus, filterSource])

  const fetchStats = useCallback(async () => {
    try {
      const res = await contactsAPI.getStats()
      setStats(res.data || {})
    } catch {}
  }, [])

  useEffect(() => { fetchContacts() }, [fetchContacts])
  useEffect(() => { fetchStats() }, [fetchStats])

  const deleteContact = async (id) => {
    if (!window.confirm('Delete this contact?')) return
    try {
      await contactsAPI.delete(id)
      toast.success('Deleted')
      fetchContacts()
      fetchStats()
    } catch { toast.error('Delete failed') }
  }

  const quickStatus = async (id, status) => {
    try {
      await contactsAPI.update(id, { status })
      setContacts(prev => prev.map(c => c._id === id ? { ...c, status } : c))
      if (status === 'spam') fetchStats()
      toast.success(`Marked as ${status}`)
    } catch { toast.error('Update failed') }
  }

  const statCards = [
    { label: 'Total', value: stats.total, color: 'from-blue-500 to-blue-600' },
    { label: 'New', value: stats.new, color: 'from-violet-500 to-violet-600' },
    { label: 'Replied', value: stats.replied, color: 'from-green-500 to-green-600' },
    { label: 'Spam', value: stats.spam, color: 'from-red-500 to-red-600' },
  ]

  return (
    <AdminLayout>
      {selected && (
        <DetailModal
          contact={selected}
          onClose={() => setSelected(null)}
          onUpdated={() => { fetchContacts(); fetchStats() }}
        />
      )}

      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-extrabold text-gray-900 dark:text-white">Contacts</h1>
            <p className="text-sm text-gray-500 mt-1">{pagination.total} total submissions</p>
          </div>
          <button onClick={() => { fetchContacts(); fetchStats() }}
            className="p-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {statCards.map(card => (
            <div key={card.label} className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{card.label}</p>
              <p className={`text-2xl font-extrabold mt-1 bg-gradient-to-r ${card.color} bg-clip-text text-transparent`}>{card.value ?? 0}</p>
            </div>
          ))}
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-4 flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-48">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input value={search} onChange={e => { setSearch(e.target.value); setPage(1) }}
              placeholder="Search name, email, phone…"
              className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
          </div>
          <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1) }}
            className="px-3 py-2.5 rounded-xl text-sm border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500">
            <option value="">All Status</option>
            {STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
          <select value={filterSource} onChange={e => { setFilterSource(e.target.value); setPage(1) }}
            className="px-3 py-2.5 rounded-xl text-sm border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500">
            <option value="">All Sources</option>
            <option value="popup">Popup</option>
            <option value="contact_page">Contact Page</option>
          </select>
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-7 h-7 animate-spin text-primary-600" />
            </div>
          ) : contacts.length === 0 ? (
            <div className="py-20 text-center">
              <AlertCircle className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No contacts found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    {['Name', 'Email', 'Phone', 'Source', 'Status', 'Date', 'Actions'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {contacts.map(c => (
                    <tr key={c._id} className={`hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors ${c.blockedEmail || c.blockedIp ? 'opacity-50' : ''}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900 dark:text-white text-sm">{c.name}</div>
                        {c.country && <div className="text-xs text-gray-400">{c.country}</div>}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        <div className="flex items-center gap-1.5">
                          {c.blockedEmail && <ShieldBan className="w-3.5 h-3.5 text-red-500 shrink-0" />}
                          {c.email}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{c.phone || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${sourceStyle[c.source] || sourceStyle.other}`}>
                          {c.source?.replace('_', ' ') || 'other'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <select value={c.status} onChange={e => quickStatus(c._id, e.target.value)}
                          className={`px-2 py-1 rounded-lg text-xs font-semibold border-0 cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary-500 ${statusStyle[c.status]}`}>
                          {STATUSES.map(s => <option key={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {format(new Date(c.createdAt), 'dd MMM yyyy')}
                        <div className="text-gray-400">{format(new Date(c.createdAt), 'h:mm a')}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button onClick={() => setSelected(c)}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors">
                            <Eye className="w-4 h-4" />
                          </button>
                          <button onClick={() => deleteContact(c._id)}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {pagination.pages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">Page {pagination.page} of {pagination.pages}</p>
            <div className="flex gap-2">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                className="p-2 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button disabled={page >= pagination.pages} onClick={() => setPage(p => p + 1)}
                className="p-2 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  )
}

export default AdminContacts
